import { useQuery } from "@tanstack/react-query";
import httpClient from "../httpClient";
import { attestedDocumentKey } from "../../constants";
import { AttestedDocument } from "../../models/attestedDocument";
import { isWellFormedReference } from "../../utils";

// Why these two numbers: a reader checking one document reloads it a handful
// of times; nobody reads ten different documents in a minute.
export const DISTINCT_REFERENCE_LIMIT = 10;
export const THROTTLE_WINDOW_MS = 60 * 1000;

// Long enough that re-reading the same document is served from cache and
// never counted a second time.
const CACHE_MS = 10 * 60 * 1000;

export type ReferenceGate = "ok" | "malformed" | "throttled";

type Lookup = { reference: string; at: number };

const STORAGE_KEY = "verifyLookups";

// Held in sessionStorage, not in a module variable: a scanned link arrives as
// a full page load, which would reset module state every time and leave the
// measure doing nothing at all.
const read = (): Lookup[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (lookups: Lookup[]) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(lookups));
  } catch {
    // A browser refusing storage loses the throttle, never the page.
  }
};

// Defence in depth, not the enumeration control: a caller who talks to the
// service directly never passes through here at all.
export const gateFor = (reference: string, now: number): ReferenceGate => {
  if (!isWellFormedReference(reference)) return "malformed";

  const seen = read().filter(entry => now - entry.at < THROTTLE_WINDOW_MS);

  if (seen.some(entry => entry.reference === reference)) {
    write(seen);
    return "ok";
  }

  if (seen.length >= DISTINCT_REFERENCE_LIMIT) {
    write(seen);
    return "throttled";
  }

  write([...seen, { reference, at: now }]);
  return "ok";
};

export const resetReferenceGate = () => {
  write([]);
};

const useQueryAttestedDocument = ({
  reference,
  authorisation = "",
  enabled = true,
}: {
  reference: string;
  authorisation?: string;
  enabled?: boolean;
}) => {
  const gate: ReferenceGate = enabled ? gateFor(reference, Date.now()) : "ok";

  const query = useQuery(
    attestedDocumentKey(reference),
    () =>
      httpClient(`attested-document/${reference}`, {
        authorisation,
      }).get<AttestedDocument>(),
    {
      staleTime: CACHE_MS,
      cacheTime: CACHE_MS,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && gate === "ok",
    }
  );

  return { ...query, gate };
};

export default useQueryAttestedDocument;
